import React from 'react';
import { View, Text } from 'react-native';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import moment from 'moment';
import styles from '../styles/FOStyles';

import T from '../Components/T';
export interface RoomBadgeData {
  isHourly: boolean;
  isMonthly: boolean;
  isRefreshment: boolean;
  isExpCO: boolean;
  isNew: boolean;
  isLinked?: boolean;
  hourlyHours?: number;
  monthlyMonths?: number;
  refreshHours?: number;
}

export const getRoomBadgesData = (room: any): RoomBadgeData | null => {
  if (!room || room.rm_status !== 'O') return null;

  const isHourly = room.cd_rentaltype === 'H';
  const isMonthly = room.cd_rentaltype === 'M';
  const isRefreshment = room.cd_rentaltype === 'R';
  const isLinked = Boolean(room.is_linked || room.isLinked);
  const isExpCO = Boolean(
    room.ci_depature &&
    moment.duration(moment().diff(room.ci_depature)).asHours() > -1
  );
  const isNew = Boolean(
    room.ci_arrvial &&
    moment.duration(moment().diff(room.ci_arrvial)).asHours() < 2 &&
    !isExpCO
  );

  if (!isHourly && !isMonthly && !isRefreshment && !isExpCO && !isNew && !isLinked) {
    return null;
  }

  return {
    isHourly,
    isMonthly,
    isRefreshment,
    isExpCO,
    isNew,
    isLinked,
    hourlyHours: room.cd_hours ? Number(room.cd_hours) : undefined,
    monthlyMonths: room.cd_months ? Number(room.cd_months) : undefined,
    refreshHours: room.cd_refreshhours
      ? Number(room.cd_refreshhours)
      : room.cd_hours
        ? Number(room.cd_hours)
        : undefined,
  };
};

export const renderRoomBadges = (room: any): React.ReactNode => {
  const badgeData = getRoomBadgesData(room);
  if (!badgeData) return null;

  return (
    <View style={styles.badgeRow}>
      {badgeData.isLinked && (
        <View style={[styles.badgePill, { backgroundColor: '#4f46e5' }]}>
          <FontAwesome name="link" size={10} color="#ffffff" />
          <Text style={styles.badgeText}><T>Linked</T></Text>
        </View>
      )}
      {badgeData.isHourly && (
        <View style={[styles.badgePill, { backgroundColor: '#0284c7' }]}>
          <FontAwesome name="clock-o" size={10} color="#ffffff" />
          <Text style={styles.badgeText}>
            <T>Hourly</T>{badgeData.hourlyHours ? ` (${badgeData.hourlyHours}h)` : ''}
          </Text>
        </View>
      )}
      {badgeData.isMonthly && (
        <View style={[styles.badgePill, { backgroundColor: '#7c3aed' }]}>
          <FontAwesome name="calendar" size={10} color="#ffffff" />
          <Text style={styles.badgeText}>
            <T>Monthly</T>{badgeData.monthlyMonths ? ` (${badgeData.monthlyMonths}m)` : ''}
          </Text>
        </View>
      )}
      {badgeData.isRefreshment && (
        <View style={[styles.badgePill, { backgroundColor: '#db2777' }]}>
          <FontAwesome name="coffee" size={10} color="#ffffff" />
          <Text style={styles.badgeText}>
            <T>Refresh</T>{badgeData.refreshHours ? ` (${badgeData.refreshHours}h)` : ''}
          </Text>
        </View>
      )}
      {badgeData.isExpCO && (
        <View style={[styles.badgePill, { backgroundColor: '#dc2626' }]}>
          <FontAwesome name="sign-out" size={10} color="#ffffff" />
          <Text style={styles.badgeText}><T>Exp. CO</T></Text>
        </View>
      )}
      {badgeData.isNew && (
        <View style={[styles.badgePill, { backgroundColor: '#059669' }]}>
          <FontAwesome name="star" size={10} color="#ffffff" />
          <Text style={styles.badgeText}><T>New</T></Text>
        </View>
      )}
    </View>
  );
};

export const getRoomStatusLabel = (room: any): string => {
  if (!room) return '';
  if (room.rm_status === 'V') return 'Vacant';
  if (
    room.rm_status === 'O' &&
    room.ci_depature &&
    moment.duration(moment().diff(room.ci_depature)).asHours() > -1
  )
    return 'Exp. Checkout';
  if (room.rm_status === 'O' && room.ci_id && Number(room.cd_rate) === 0)
    return 'House Guest';
  if (room.rm_status === 'O' && room.ci_id && room.id_count === 1)
    return 'Occupied';
  if (room.rm_status === 'O')
    return 'Group';
  if (room.rm_status === 'D') return 'Dirty';
  if (room.rm_status === 'B') return 'Block';
  return 'Repair';
};

export const getRoomColor = (room: any, gdata: any): string => {
  if (!room) return '#ffffff';
  if (room.rm_status === 'V') return gdata?.vacant || '#88E2F7';
  if (
    room.rm_status === 'O' &&
    room.ci_depature &&
    moment.duration(moment().diff(room.ci_depature)).asHours() > -1
  )
    return gdata?.expcheckout || '#E2B2EA';
  if (room.rm_status === 'O' && room.ci_id && Number(room.cd_rate) === 0)
    return gdata?.houseguest || '#CAC9EF';
  if (room.rm_status === 'O' && room.ci_id && room.id_count === 1)
    return gdata?.occupied || '#8BF788';
  if (room.rm_status === 'O' && room.ci_id && room.id_count > 1)
    return gdata?.group || '#E8EAB2';
  if (room.rm_status === 'O' && !room.ci_id) return gdata?.group || '#E8EAB2';
  if (room.rm_status === 'D') return gdata?.dirty || '#EADAEA';
  if (room.rm_status === 'B') return gdata?.block || '#F9F369';
  return gdata?.maintenance || '#EAB2D2';
};

export const filterRoomsList = (
  roomsData: any[],
  filter: number,
  listType: number,
): any[] => {
  if (!roomsData) return [];

  return roomsData
    .filter(
      v =>
        filter === 1 ||
        (filter === 2 && v.rm_status === 'V') ||
        (filter === 3 && v.rm_status === 'O') ||
        (filter === 4 && v.rm_status === 'D') ||
        (filter === 5 && v.rm_status === 'B') ||
        (filter === 6 && v.rm_status === 'M') ||
        (filter === 7 &&
          v.rm_status === 'O' &&
          v.ci_depature &&
          moment.duration(moment().diff(v.ci_depature)).asHours() > -1) ||
        (filter === 8 && v.rm_status === 'O' && v.cd_rentaltype === 'H') ||
        (filter === 9 && v.rm_status === 'O' && v.cd_rentaltype === 'M') ||
        (filter === 10 && v.rm_status === 'O' && v.cd_rentaltype === 'R'),
    )
    .slice()
    .sort((a, b) => {
      const aStr =
        (a.rm_building || '') + (listType === 2 ? (a.rt_name || '') : '') + (a.rm_name || '');
      const bStr =
        (b.rm_building || '') + (listType === 2 ? (b.rt_name || '') : '') + (b.rm_name || '');
      return aStr.localeCompare(bStr);
    });
};
